import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  PenTool, Plus, Search, Clock, CheckCircle2, XCircle, Send,
  Eye, RefreshCw, FileText, Shield, AlertTriangle, Copy
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  awaiting_signature: { label: 'Awaiting', color: 'bg-amber-100 text-amber-700', icon: Clock },
  signed: { label: 'Signed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-gray-100 text-gray-500', icon: AlertTriangle },
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function ESignaturePage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(null);
  const [form, setForm] = useState({ document_title: '', signer_name: '', signer_email: '' });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/signatures`);
      setRequests(data.requests || []);
    } catch { toast.error('Failed to load signatures'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.document_title.trim() || !form.signer_name.trim()) { toast.error('Title and signer name required'); return; }
    try {
      await axios.post(`${API}/api/signatures/request`, form);
      toast.success('Signature request created!');
      setShowCreate(false);
      setForm({ document_title: '', signer_name: '', signer_email: '' });
      fetchData();
    } catch { toast.error('Failed to create'); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/sign/${token}`);
    toast.success('Signing link copied!');
  };

  const filtered = requests.filter(r =>
    !search || r.document_title?.toLowerCase().includes(search.toLowerCase()) || r.signer_name?.toLowerCase().includes(search.toLowerCase())
  );

  const counts = {
    total: requests.length,
    awaiting: requests.filter(r => r.status === 'awaiting_signature').length,
    signed: requests.filter(r => r.status === 'signed').length,
    declined: requests.filter(r => r.status === 'declined').length,
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="esignature-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">E-Signatures<PageInfoTooltip pageKey="e-signature" /></h1>
          <p className="text-gray-medium text-sm mt-1">Send and track document signature requests</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-signature-btn">
          <Plus className="h-4 w-4 mr-1" /> Request Signature
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={PenTool} label="Total Requests" value={counts.total} color="bg-blue-50 text-blue-600" />
        <StatCard icon={Clock} label="Awaiting" value={counts.awaiting} color="bg-amber-50 text-amber-600" />
        <StatCard icon={CheckCircle2} label="Signed" value={counts.signed} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={XCircle} label="Declined" value={counts.declined} color="bg-red-50 text-red-600" />
      </div>

      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search by document or signer..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="signature-search" />
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-medium">
              <PenTool className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No signature requests yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(req => {
                const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.awaiting_signature;
                return (
                  <div key={req.id || req.token} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => setShowView(req)} data-testid={`signature-row-${req.id || req.token}`}>
                    <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <PenTool className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-text-primary truncate">{req.document_title}</span>
                        <Badge className={cn("text-[10px]", cfg.color)}>{cfg.label}</Badge>
                      </div>
                      <p className="text-xs text-gray-medium">{req.signer_name} {req.signer_email ? `· ${req.signer_email}` : ''}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-gray-medium">{req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={e => { e.stopPropagation(); copyLink(req.token); }}><Copy className="h-3.5 w-3.5" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request Signature</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Document Title *</Label><Input value={form.document_title} onChange={e => setForm(f => ({ ...f, document_title: e.target.value }))} data-testid="sig-doc-title" /></div>
            <div><Label>Signer Name *</Label><Input value={form.signer_name} onChange={e => setForm(f => ({ ...f, signer_name: e.target.value }))} data-testid="sig-signer-name" /></div>
            <div><Label>Signer Email</Label><Input value={form.signer_email} onChange={e => setForm(f => ({ ...f, signer_email: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="send-signature-btn">Send Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!showView} onOpenChange={() => setShowView(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{showView?.document_title}</DialogTitle></DialogHeader>
          {showView && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-medium">Signer:</span> <span className="font-medium">{showView.signer_name}</span></div>
                <div><span className="text-gray-medium">Email:</span> <span>{showView.signer_email || '—'}</span></div>
                <div><span className="text-gray-medium">Status:</span> <Badge className={cn("text-[10px]", (STATUS_CONFIG[showView.status] || STATUS_CONFIG.awaiting_signature).color)}>{(STATUS_CONFIG[showView.status] || STATUS_CONFIG.awaiting_signature).label}</Badge></div>
                <div><span className="text-gray-medium">Created:</span> <span>{new Date(showView.created_at).toLocaleDateString()}</span></div>
                {showView.signed_at && <div><span className="text-gray-medium">Signed:</span> <span>{new Date(showView.signed_at).toLocaleDateString()}</span></div>}
                <div><span className="text-gray-medium">Expires:</span> <span>{showView.expires_at ? new Date(showView.expires_at).toLocaleDateString() : '—'}</span></div>
              </div>
              {showView.audit_trail?.length > 0 && (
                <div>
                  <p className="font-medium text-text-primary mb-1">Audit Trail</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {showView.audit_trail.map((evt, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs p-2 bg-gray-50 rounded">
                        <Shield className="h-3 w-3 text-gray-400 flex-shrink-0" />
                        <span className="capitalize font-medium">{evt.event}</span>
                        <span className="text-gray-400">·</span>
                        <span className="text-gray-medium">{new Date(evt.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button size="sm" variant="outline" onClick={() => copyLink(showView.token)}><Copy className="h-3.5 w-3.5 mr-1" />Copy Signing Link</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
